import { t } from '@procumi/shared';
import { useEffect, useRef, type ReactNode } from 'react';
import { ask } from '../lib/confirm';

/**
 * Învelitoare peste <dialog>: se deschide modal și se închide și cu Escape.
 *
 * Atenție la un detaliu care nu se vede în producție: în `StrictMode`, React
 * rulează efectul de două ori (efect, curățare, efect). Curățarea închide
 * dialogul, iar `close()` pune evenimentul `close` în coadă — acesta ajunge
 * *după* ce a doua rulare a atașat din nou ascultătorul, care altfel ar chema
 * `onClose` și ar închide dialogul imediat după ce s-a deschis. De aceea ținem
 * minte, într-un ref care supraviețuiește ambelor rulări, că noi am închis
 * dialogul, și ignorăm acel eveniment.
 */
/**
 * Întrebarea dinainte de a pierde ce s-a scris. Fără nimic scris, nu întreabă:
 * o confirmare care apare mereu ajunge să fie apăsată fără citit.
 */
export function confirmDiscard(dirty: boolean): Promise<boolean> {
  if (!dirty) return Promise.resolve(true);
  return ask({
    title: t('Închizi fără să salvezi?'),
    message: t('Ce ai scris în fereastra asta se pierde.'),
    confirm: t('Închide fără salvare'),
    cancel: t('Continuă'),
    danger: true,
  });
}

export function Dialog({
  children,
  onClose,
  dirty = false,
  wide = false,
}: {
  children: ReactNode;
  onClose: () => void;
  /** s-a scris ceva nesalvat: Escape întreabă înainte să închidă */
  dirty?: boolean;
  /** formular cu tabel (comanda): mai lat decât dialogul obișnuit */
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const isDirty = useRef(dirty);
  isDirty.current = dirty;
  const closedByUs = useRef(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;

    const onCloseEvent = () => {
      if (closedByUs.current) {
        closedByUs.current = false;
        return;
      }
      close.current();
    };

    // Escape trimite întâi `cancel`. Cu ceva scris, îl oprim și întrebăm;
    // dacă omul confirmă, închidem noi.
    const onCancel = (e: Event) => {
      if (!isDirty.current) return;
      e.preventDefault();
      void confirmDiscard(true).then((ok) => {
        if (ok) close.current();
      });
    };

    d.addEventListener('close', onCloseEvent);
    d.addEventListener('cancel', onCancel);
    if (!d.open) d.showModal();

    return () => {
      d.removeEventListener('close', onCloseEvent);
      d.removeEventListener('cancel', onCancel);
      if (d.open) {
        closedByUs.current = true;
        d.close();
      }
    };
  }, []);

  // × din colț: la fel ca Escape, întreabă întâi dacă s-a scris ceva.
  const tryClose = () => {
    void confirmDiscard(isDirty.current).then((ok) => {
      if (ok) close.current();
    });
  };

  return (
    <dialog ref={ref} className={wide ? 'wide' : undefined}>
      <button type="button" className="dlg-x" aria-label={t('Închide')} onClick={tryClose}>
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      {children}
    </dialog>
  );
}
